/**
 * 右上角常驻的**全局归属**筛选栏：品牌 + 店铺（R12；ux-patterns §9 第 4 条）。
 *
 * ## 它为什么在顶栏
 *
 * 「品牌 / 站点」是**全局归属**（我在看哪个品牌的哪个店），不是某块数据的口径声明；
 * 时间范围/语言这类"页面上下文"必须留在数据区左上角，否则切看板时会让人误读区间。
 *
 * ## 行为
 *
 *  · 品牌来自 `/v1/brands`（归档品牌不进选择器），店铺来自 `/v1/shops`（按品牌过滤）；
 *  · 数据到达后调用 store 的 `reconcileBrands` / `reconcileShops` 做**默认值收敛**：
 *    单品牌 → 默认选它；店铺默认「全部店铺」；切换品牌后不属于该品牌的店铺回落「全部店铺」；
 *  · 选择写入 localStorage（`store/contextStore.ts`），**刷新与跨页导航后保持**。
 *
 * ## C1 Phase 3：按权限过滤（`/v1/shops` 已由服务端裁剪）
 *
 *  · **未授权店铺根本不出现**（服务端只返回可见店铺；前端不另维护一份可见集合）；
 *  · **一个店铺都没有** ⇒ 显示「你没有任何店铺权限，请联系管理员」，**不是空下拉**；
 *  · 当前店铺在权限变更后不可见 ⇒ **自动切到第一个可见店铺**（`pickShopAfterPermissionChange`，
 *    纯函数在 `lib/permissions.ts`，这里只调用）。
 *
 * ## 加载/失败时的显示纪律
 *
 * 数据没到之前下拉是 **disabled** 的：显示一个可选的"全部品牌"会让人以为"品牌只有全部"，
 * 而真实情况是"还没读到"。读取失败给一个可点的重试，不静默假装没有品牌。
 *
 * ## 结构
 *
 * `GlobalFilterBarView` 是**纯展示**（props 进、JSX 出，无 hook、无取数）——结构测试直接渲染它；
 * `GlobalFilterBar` 只负责取数 / 默认值收敛 / 自动切换副作用。
 */

import { useEffect, type ReactElement } from "react";
import { useApiResource } from "../hooks/useApiResource.js";
import { apiFetchBrands, apiFetchShops } from "../lib/apiClient.js";
import { ALL_BRANDS, ALL_SHOPS, brandOptions, shopLabel, shopsForBrand, type ShopOptionSource } from "../lib/globalContext.js";
import { pickShopAfterPermissionChange } from "../lib/permissions.js";
import { useContextStore } from "../store/contextStore.js";

const SELECT_CLASS =
  "max-w-40 truncate rounded-control border border-line bg-surface px-1.5 text-meta text-ink outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-50";

export interface GlobalFilterBarViewProps {
  readonly brandsReady: boolean;
  readonly brandItems: readonly { readonly brandId: string; readonly name: string }[];
  readonly brandId: string;
  readonly onBrandChange: (brandId: string) => void;
  readonly shopsReady: boolean;
  /** 全部**可见**店铺（服务端 `GET /v1/shops` 已按权限裁剪）。 */
  readonly visibleShops: readonly ShopOptionSource[];
  /** 当前品牌下的可见店铺（选择器选项 = 它 + 「全部店铺」）。 */
  readonly shopItems: readonly ShopOptionSource[];
  readonly shopId: string;
  readonly onShopChange: (shopId: string) => void;
  readonly failed: boolean;
  readonly onRetry: () => void;
}

/** 纯展示：品牌 / 店铺两个选择器 + 「无店铺权限」提示 + 失败重试（结构测试直接用它）。 */
export function GlobalFilterBarView({
  brandsReady,
  brandItems,
  brandId,
  onBrandChange,
  shopsReady,
  visibleShops,
  shopItems,
  shopId,
  onShopChange,
  failed,
  onRetry,
}: GlobalFilterBarViewProps): ReactElement {
  /** 一个店铺权限都没有：**显示明确提示**，而不是一个空下拉（设计稿 §3.2-1）。 */
  const noShopPermission = shopsReady && visibleShops.length === 0;

  return (
    <span className="flex items-center gap-1.5">
      <select
        aria-label="品牌筛选"
        className={SELECT_CLASS}
        style={{ height: "var(--control-height)" }}
        value={brandId}
        disabled={!brandsReady}
        title={brandsReady ? "全局品牌（驱动广告管理 / 货号看板）" : "正在读取品牌…"}
        onChange={(event) => onBrandChange(event.target.value)}
      >
        <option value={ALL_BRANDS}>全部品牌</option>
        {brandItems.map((brand) => (
          <option key={brand.brandId} value={brand.brandId}>
            {brand.name}
          </option>
        ))}
      </select>

      {noShopPermission ? (
        <span
          data-no-shop-permission="true"
          className="rounded-control border border-warning/40 px-1.5 text-2xs text-warning-text"
          style={{ height: "var(--control-height)", lineHeight: "var(--control-height)" }}
          title="你的账号没有任何店铺权限：所有店铺数据都不可见（服务端 fail-closed）。请联系管理员授权。"
        >
          你没有任何店铺权限，请联系管理员
        </span>
      ) : (
        <select
          aria-label="店铺筛选"
          className={SELECT_CLASS}
          style={{ height: "var(--control-height)" }}
          value={shopId}
          disabled={!shopsReady}
          title={shopsReady ? "全局店铺（驱动广告管理 / 货号看板）" : "正在读取店铺…"}
          onChange={(event) => onShopChange(event.target.value)}
        >
          <option value={ALL_SHOPS}>全部店铺</option>
          {shopItems.map((shop) => (
            <option key={shop.shopId} value={shop.shopId}>
              {shopLabel(shop)}
            </option>
          ))}
        </select>
      )}

      {failed ? (
        <button
          type="button"
          className="rounded-control border border-warning/40 px-1.5 text-2xs text-warning-text hover:bg-hover"
          style={{ height: "var(--control-height)" }}
          title="品牌/店铺清单读取失败；筛选值仍然是上次选择"
          onClick={onRetry}
        >
          品牌/店铺读取失败，重试
        </button>
      ) : null}
    </span>
  );
}

export function GlobalFilterBar(): ReactElement {
  const brands = useApiResource((signal) => apiFetchBrands(signal), []);
  const shops = useApiResource((signal) => apiFetchShops(signal), []);

  const brandId = useContextStore((state) => state.brandId);
  const shopId = useContextStore((state) => state.shopId);
  const setBrandId = useContextStore((state) => state.setBrandId);
  const setShopId = useContextStore((state) => state.setShopId);
  const reconcileBrands = useContextStore((state) => state.reconcileBrands);
  const reconcileShops = useContextStore((state) => state.reconcileShops);
  const syncShopFromUrl = useContextStore((state) => state.syncShopFromUrl);

  /**
   * ⭐ 2026-10-07 URL 携带站点：**URL = 真相，store = 镜像**。
   *  · 选择器点击 ⇒ `setShopId`（**先写 URL**，再镜像到 store）——切换器只"写 URL"这一条路径；
   *  · 浏览器前进/后退 / 直接改 URL ⇒ 下面这个监听把 URL **镜像**进 store（只读 URL、只写 store）。
   * ⇒ 不存在"两个 effect 互相写"（本会话「花费总览切店铺卡死」的根因）。
   */
  useEffect(() => {
    const onPopState = (): void => syncShopFromUrl();
    window.addEventListener("popstate", onPopState);
    // 首帧也镜像一次（覆盖"外部链接直接打开带 shopId 的 URL"）。
    syncShopFromUrl();
    return () => window.removeEventListener("popstate", onPopState);
  }, [syncShopFromUrl]);

  // 数据到达后收敛默认值（store 内部对"同值"短路，不会造成额外渲染）。
  useEffect(() => {
    if (brands.data !== null) reconcileBrands(brands.data.items);
  }, [brands.data, reconcileBrands]);
  useEffect(() => {
    if (shops.data !== null) reconcileShops(shops.data.items);
  }, [shops.data, reconcileShops]);

  /**
   * C1 Phase 3：**当前店铺在权限变更后不可见 ⇒ 自动切到第一个可见店铺**。
   *
   * 与 `reconcileShops` 的分工（不冲突）：
   *  · `reconcileShops` 处理**品牌**维度（当前店不属于所选品牌 → 回落「全部店铺」）；
   *  · 本效果只处理**权限**维度：当前店不在 `GET /v1/shops` 返回的**可见集合**里
   *    （被撤销授权 / 店铺被删）→ 切到"当前品牌下第一个可见店"，而不是留在不可见店上报 403。
   *  · 可见集合由**服务端**裁剪（Phase 2B），前端不另维护一份（设计稿 §3.2-1）。
   */
  useEffect(() => {
    const visible = shops.data?.items ?? null;
    if (visible === null || visible.length === 0) return;
    const brandScoped = shopsForBrand(visible, brandId);
    const next = pickShopAfterPermissionChange(
      visible.map((shop) => shop.shopId),
      brandScoped.map((shop) => shop.shopId),
      shopId,
    );
    if (next !== null) setShopId(next);
  }, [shops.data, shopId, brandId, setShopId]);

  const brandItems = brands.data === null ? [] : brandOptions(brands.data.items);
  const shopItems = shops.data === null ? [] : shopsForBrand(shops.data.items, brandId);

  return (
    <GlobalFilterBarView
      brandsReady={brands.data !== null}
      brandItems={brandItems}
      brandId={brandId}
      onBrandChange={setBrandId}
      shopsReady={shops.data !== null}
      visibleShops={shops.data?.items ?? []}
      shopItems={shopItems}
      shopId={shopId}
      onShopChange={setShopId}
      failed={brands.state === "error" || shops.state === "error"}
      onRetry={() => {
        brands.reload();
        shops.reload();
      }}
    />
  );
}
